import { AppId } from '@inno-tools/container-bridge'
import {
  AccountingApp,
  AfterSalesApp,
  ApplicationStudioApp,
  BulkMailingApp,
  ByteBuilderApp,
  CalendarApp,
  ClusterCellsApp,
  ClusterCloudApp,
  ClusterCodeApp,
  ClusterDocApp,
  ClusterFilesApp,
  ClusterFoneApp,
  ClusterMeetApp,
  ClusterPdfApp,
  ClusterSlidesApp,
  ClusterSupportApp,
  CompanySearcherApp,
  ContractManagementApp,
  CoursesApp,
  DocAiApp,
  EmailApp,
  ExternalFormsApp,
  GdprApp,
  HRApp,
  IntegrationApp,
  IntegrationAppV2,
  InventoryApp,
  IpBoxApp,
  LiveChatApp,
  MyOrganizationApp,
  OrderManagementApp,
  ProfileApp,
  ProjectManagementApp,
  RecruitingAgentApp,
  RiskManagementApp,
  SalesApp,
  SubsidiesApp,
  TaskManagementApp,
  TemplateManagerApp,
  TimeTrackingApp,
  TimeTrackingConsentModals,
  TransferPricingApp,
  UsageAnalyticsApp,
  WorkflowsApp
} from 'modules/app-registry/components'
import { AssetManagementApp } from 'modules/app-registry/components/AssetManagementApp'
import { ClusterMeetCallModal } from 'modules/app-registry/components/ClusterMeetApp/modals/ClusterMeetCallModal'
import { EmployeesNotificationsConsentModal } from 'modules/app-registry/components/HRApp/modals'
import { OnboardingApp } from 'modules/app-registry/components/OnboardingApp/OnboardingApp'
import { ProjectManagementAppV2 } from 'modules/app-registry/components/ProjectManagementAppV2'
import { SettingsApp } from 'modules/app-registry/components/SettingsApp'
import { TimeTrackingRichAppIcon } from 'modules/app-registry/components/TimetrackingApp/app-icon/TimeTrackingRichAppIcon'
import { AppGroup } from 'shared/constants/apps'
import { AppConfig, AppConfigsType } from 'shared/types/app'

import { i18nextContainer } from './i18n/config'
import ClusterDocIllustration from '/public/app-illustrations/clusterdoc.webp'
import DocsIllustration from '/public/app-illustrations/docs.webp'
import HrIllustration from '/public/app-illustrations/hr.webp'
import ProjectManagementIllustration from '/public/app-illustrations/project_management.webp'
import TaskManagementIllustration from '/public/app-illustrations/task_management.webp'
import TimeTrackingIllustration from '/public/app-illustrations/time_tracking.webp'

const isAppConfigured = (config: AppConfig): boolean => {
  // Either remote source is enough: CDN-served apps may ship without a
  // direct federationUrl fallback.
  if (
    !isNonEmptyString(config.federationUrl) &&
    !isNonEmptyString(config.cdnUrl)
  ) {
    return false
  }

  if (typeof window !== 'undefined') {
    if (!isNonEmptyString(window.__MFE_ENV_SCRIPTS__?.[config.runtimeEnvKey])) {
      return false
    }
  }

  return true
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== ''

const rawAppConfigs: AppConfigsType = {
  /**
   * Files Apps
   */
  [AppId.CLUSTER_FILES]: {
    id: AppId.CLUSTER_FILES,
    component: ClusterFilesApp,
    path: '/cluster-files',
    icon: '/app-icons/files.webp',
    appIcon: '/app-icons/files.webp',
    placement: ['launchpad'],
    label: 'Files',
    group: AppGroup.OFFICE_TOOLS,
    slug: 'files',
    description: i18nextContainer.t('appsList.clusterFiles.description'),
    name: 'Files',
    illustrationUrl: DocsIllustration,
    routeProps: {},
    federationUrl: process.env.REACT_APP_CLUSTER_FILES_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_CLUSTER_FILES_APP_CDN_URL,
    runtimeEnvKey: 'clusterFiles'
  },

  /**
   * Workforce Management Apps
   */

  [AppId.SUBSIDIES]: {
    id: AppId.SUBSIDIES,
    component: SubsidiesApp,
    path: '/compliance-center',
    icon: '/app-icons/subsidies.webp',
    appIcon: '/app-icons/subsidies.webp',
    isPinned: true,
    placement: ['launchpad'],
    label: 'Compliance Center',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    name: 'Compliance Center',
    description: i18nextContainer.t('appsList.subsidies.description'),
    slug: 'subsidies',
    routeProps: {},
    federationUrl: process.env.REACT_APP_SUBSIDIES_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_SUBSIDIES_APP_CDN_URL,
    runtimeEnvKey: 'subsidiesApp'
  },
  [AppId.HR]: {
    id: AppId.HR,
    component: HRApp,
    path: '/hr',
    icon: '/app-icons/hr.webp',
    appIcon: '/app-icons/hr.webp',
    placement: ['launchpad'],
    label: 'Human Resources',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    name: 'HR',
    description: i18nextContainer.t('appsList.hr.description'),
    slug: 'hr',
    illustrationUrl: HrIllustration,
    routeProps: {},
    modals: [EmployeesNotificationsConsentModal],
    federationUrl: process.env.REACT_APP_EMPLOYEES_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_EMPLOYEES_APP_CDN_URL,
    runtimeEnvKey: 'employeesApp'
  },
  [AppId.INVENTORY]: {
    id: AppId.INVENTORY,
    component: InventoryApp,
    path: '/inventory',
    icon: '/app-icons/inventory.webp',
    appIcon: '/app-icons/inventory.webp',
    placement: ['launchpad'],
    name: 'Inventory',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    label: 'Inventory',
    slug: 'inventory',
    description: i18nextContainer.t('appsList.inventory.description'),
    routeProps: {
      exact: false
    },
    federationUrl: process.env.REACT_APP_INVENTORY_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_INVENTORY_APP_CDN_URL,
    runtimeEnvKey: 'inventoryApp'
  },
  [AppId.PROJECT_MANAGEMENT]: {
    id: AppId.PROJECT_MANAGEMENT,
    component: ProjectManagementApp,
    path: '/project-management',
    icon: '/app-icons/project-management.webp',
    appIcon: '/app-icons/project-management.webp',
    placement: ['launchpad'],
    name: 'Project Management',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    label: 'Project Management',
    description: i18nextContainer.t('appsList.project-management.description'),
    slug: 'external_projects',
    illustrationUrl: ProjectManagementIllustration,
    routeProps: {},
    federationUrl: process.env.REACT_APP_PROJECT_MANAGEMENT_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_PROJECT_MANAGEMENT_APP_CDN_URL,
    runtimeEnvKey: 'projectManagementApp'
  },
  [AppId.IP_BOX]: {
    id: AppId.IP_BOX,
    component: IpBoxApp,
    path: '/ip-box',
    icon: '/app-icons/inventory.webp',
    appIcon: '/app-icons/inventory.webp',
    placement: ['launchpad'],
    name: 'IP Box',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    label: 'IP Box',
    slug: 'ip_box',
    routeProps: {
      exact: false
    },
    federationUrl: process.env.REACT_APP_IP_BOX_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_IP_BOX_APP_CDN_URL,
    runtimeEnvKey: 'ipBoxApp'
  },
  [AppId.PROJECT_MANAGEMENT_V2]: {
    id: AppId.PROJECT_MANAGEMENT_V2,
    component: ProjectManagementAppV2,
    path: '/project-management-v2',
    icon: '/app-icons/project-management.webp',
    appIcon: '/app-icons/project-management.webp',
    placement:
      process.env.REACT_APP_INCLUDE_PROJECT_MANAGEMENT_V2_IN_LAUNCHPAD !==
      'false'
        ? ['launchpad']
        : [],
    name: 'Project Management V2',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    label: 'Project Management V2',
    description: i18nextContainer.t('appsList.project-management.description'),
    slug: 'external_projects',
    illustrationUrl: ProjectManagementIllustration,
    routeProps: {},
    federationUrl: process.env.REACT_APP_PROJECT_MANAGEMENT_V2_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_PROJECT_MANAGEMENT_V2_APP_CDN_URL,
    runtimeEnvKey: 'projectManagementAppV2'
  },
  [AppId.TASK_MANAGEMENT]: {
    id: AppId.TASK_MANAGEMENT,
    component: TaskManagementApp,
    path: '/task-management',
    icon: '/app-icons/task-management.webp',
    appIcon: '/app-icons/task-management.webp',
    name: 'Task Management',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    placement: ['launchpad'],
    label: 'Task Management',
    description: i18nextContainer.t('appsList.taskManagement.description'),
    slug: 'task_management_v2',
    illustrationUrl: TaskManagementIllustration,
    routeProps: {},
    federationUrl: process.env.REACT_APP_TASKMANAGEMENT_APP_V2_DEV_PATH,
    cdnUrl: process.env.REACT_APP_TASKMANAGEMENT_APP_V2_CDN_URL,
    runtimeEnvKey: 'taskManagementAppV2'
  },
  [AppId.TIMETRACKING]: {
    id: AppId.TIMETRACKING,
    component: TimeTrackingApp,
    path: '/timetracking',
    icon: '/app-icons/time-tracking.webp',
    appIcon: '/app-icons/time-tracking.webp',
    placement: ['launchpad'],
    name: 'Time Tracking',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    label: 'Time Tracking',
    description: i18nextContainer.t('appsList.timetracking.description'),
    slug: 'time_tracking',
    illustrationUrl: TimeTrackingIllustration,
    richAppIcon: TimeTrackingRichAppIcon,
    routeProps: {},
    modals: [TimeTrackingConsentModals],
    federationUrl: process.env.REACT_APP_TIMETRACKING_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_TIMETRACKING_APP_CDN_URL,
    runtimeEnvKey: 'timeTrackingApp'
  },
  [AppId.CLUSTER_SUPPORT]: {
    id: AppId.CLUSTER_SUPPORT,
    component: ClusterSupportApp,
    path: '/cluster-support',
    icon: '/app-icons/cluster-support.webp',
    appIcon: '/app-icons/cluster-support.webp',
    placement: ['launchpad'],
    name: 'Cluster Support',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    label: 'Cluster Support',
    slug: 'cluster_support',
    routeProps: {},
    federationUrl: process.env.REACT_APP_CLUSTER_SUPPORT_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_CLUSTER_SUPPORT_APP_CDN_URL,
    runtimeEnvKey: 'clusterSupportApp'
  },
  [AppId.CALENDAR]: {
    id: AppId.CALENDAR,
    component: CalendarApp,
    path: '/calendar',
    icon: '/app-icons/calendar.webp',
    appIcon: '/app-icons/calendar.webp',
    placement: ['launchpad'],
    name: 'Calendar',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    label: 'Calendar',
    slug: 'calendar',
    routeProps: {
      exact: true
    },
    federationUrl: process.env.REACT_APP_CALENDAR_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_CALENDAR_APP_CDN_URL,
    runtimeEnvKey: 'calendarApp'
  },
  [AppId.RECRUITING_AGENT]: {
    id: AppId.RECRUITING_AGENT,
    component: RecruitingAgentApp,
    path: '/recruiting-agent',
    icon: '/app-icons/hr.webp',
    appIcon: '/app-icons/hr.webp',
    placement: ['launchpad'],
    name: 'Recruiting Agent',
    group: AppGroup.WORKFORCE_MANAGEMENT,
    label: 'Recruiting Agent',
    slug: 'recruiting_agent',
    routeProps: {},
    federationUrl: process.env.REACT_APP_RECRUITING_AGENT_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_RECRUITING_AGENT_APP_CDN_URL,
    runtimeEnvKey: 'recruitingAgentApp'
  },
  /**
   * Office Tools Apps
   */
  [AppId.CLUSTER_DOCS]: {
    id: AppId.CLUSTER_DOCS,
    component: ClusterDocApp,
    path: '/documents',
    icon: '/app-icons/docs.webp',
    appIcon: '/app-icons/docs.webp',
    placement: ['launchpad'],
    name: 'Documents',
    group: AppGroup.OFFICE_TOOLS,
    label: 'Documents',
    slug: 'office',
    description: i18nextContainer.t('appsList.clusterOffice.description'),
    illustrationUrl: ClusterDocIllustration,
    routeProps: {},
    federationUrl: process.env.REACT_APP_CLUSTER_OFFICE_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_CLUSTER_OFFICE_APP_CDN_URL,
    runtimeEnvKey: 'clusterOffice'
  },
  [AppId.CLUSTER_CELLS]: {
    id: AppId.CLUSTER_CELLS,
    component: ClusterCellsApp,
    path: '/cells',
    icon: '/app-icons/spreadsheets.webp',
    appIcon: '/app-icons/spreadsheets.webp',
    placement: ['launchpad'],
    name: 'Cells',
    group: AppGroup.OFFICE_TOOLS,
    label: 'Cells',
    slug: 'office',
    description: i18nextContainer.t('appsList.clusterOffice.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_CLUSTER_OFFICE_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_CLUSTER_OFFICE_APP_CDN_URL,
    runtimeEnvKey: 'clusterOffice'
  },
  [AppId.CLUSTER_SLIDES]: {
    id: AppId.CLUSTER_SLIDES,
    component: ClusterSlidesApp,
    path: '/slides',
    icon: '/app-icons/slides.webp',
    appIcon: '/app-icons/slides.webp',
    placement: ['launchpad'],
    name: 'Slides',
    group: AppGroup.OFFICE_TOOLS,
    label: 'Slides',
    slug: 'office',
    description: i18nextContainer.t('appsList.clusterOffice.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_CLUSTER_OFFICE_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_CLUSTER_OFFICE_APP_CDN_URL,
    runtimeEnvKey: 'clusterOffice'
  },
  [AppId.CLUSTER_PDF]: {
    id: AppId.CLUSTER_PDF,
    component: ClusterPdfApp,
    path: '/pdfs',
    icon: '/app-icons/pdf.webp',
    appIcon: '/app-icons/pdf.webp',
    placement: ['launchpad'],
    name: 'PDFs',
    group: AppGroup.OFFICE_TOOLS,
    label: 'PDFs',
    slug: 'office',
    description: i18nextContainer.t('appsList.clusterOffice.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_CLUSTER_OFFICE_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_CLUSTER_OFFICE_APP_CDN_URL,
    runtimeEnvKey: 'clusterOffice'
  },
  [AppId.EXTERNAL_FORMS]: {
    id: AppId.EXTERNAL_FORMS,
    component: ExternalFormsApp,
    path: '/external-forms',
    icon: '/app-icons/forms.webp',
    appIcon: '/app-icons/forms.webp',
    placement: ['launchpad'],
    name: 'External Forms',
    group: AppGroup.OFFICE_TOOLS,
    label: 'Cluster Forms',
    slug: 'external_forms',
    description: i18nextContainer.t('appsList.externalForms.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_EXTERNAL_FORMS_APP_DEV_PATH,
    runtimeEnvKey: 'externalFormsApp'
  },
  [AppId.CLUSTER_CODE]: {
    id: AppId.CLUSTER_CODE,
    component: ClusterCodeApp,
    path: '/cluster-code',
    icon: '/app-icons/cluster-code.webp',
    appIcon: '/app-icons/cluster-code.webp',
    slug: 'code',
    placement: ['launchpad'],
    name: 'Code',
    label: 'Code',
    routeProps: {},
    group: AppGroup.OFFICE_TOOLS,
    federationUrl: process.env.REACT_APP_CLUSTER_CODE_APP_DEV_PATH,
    runtimeEnvKey: 'clusterCode'
  },
  [AppId.COURSES]: {
    id: AppId.COURSES,
    component: CoursesApp,
    path: '/courses',
    icon: '/app-icons/courses.webp',
    appIcon: '/app-icons/courses.webp',
    isPinned: true,
    placement: ['launchpad'],
    label: 'Courses',
    group: AppGroup.OFFICE_TOOLS,
    name: 'Courses',
    description: i18nextContainer.t('appsList.courses.description'),
    slug: 'courses_app',
    routeProps: {},
    federationUrl: process.env.REACT_APP_COURSES_APP_DEV_PATH,
    runtimeEnvKey: 'coursesAppV2'
  },
  [AppId.ONBOARDING]: {
    id: AppId.ONBOARDING,
    component: OnboardingApp,
    path: '/onboarding',
    icon: '/app-icons/onboarding.webp',
    appIcon: '/app-icons/onboarding.webp',
    isPinned: true,
    placement: ['launchpad'],
    label: 'Onboarding',
    group: AppGroup.OFFICE_TOOLS,
    name: 'Onboarding',
    description: i18nextContainer.t('appsList.courses.description'),
    slug: 'onboarding',
    routeProps: {},
    federationUrl: process.env.REACT_APP_ONBOARDING_APP_DEV_PATH,
    runtimeEnvKey: 'onboardingAppClient'
  },
  /**
   * Communication Apps
   */
  [AppId.GDPR]: {
    id: AppId.GDPR,
    component: GdprApp,
    path: '/contacts',
    icon: '/app-icons/contacts.webp',
    appIcon: '/app-icons/contacts.webp',
    placement: ['launchpad'],
    name: 'Contacts',
    group: AppGroup.COMMUNICATION,
    label: 'Contacts',
    slug: 'gdpr',
    routeProps: {},
    federationUrl: process.env.REACT_APP_GDPR_APP_DEV_PATH,
    runtimeEnvKey: 'contactPersonGdpr'
  },
  [AppId.CLUSTER_MEET]: {
    id: AppId.CLUSTER_MEET,
    component: ClusterMeetApp,
    path: '/cluster-meet',
    icon: '/app-icons/meet.webp',
    appIcon: '/app-icons/meet.webp',
    placement: ['launchpad'],
    name: 'Cluster Meet',
    group: AppGroup.COMMUNICATION,
    label: 'Cluster Meet',
    slug: 'meet',
    routeProps: {},
    modals: [ClusterMeetCallModal],
    federationUrl: process.env.REACT_APP_CLUSTER_MEET_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_CLUSTER_MEET_APP_CDN_URL,
    runtimeEnvKey: 'meetFrontendMonorepoV2'
  },
  [AppId.CLUSTER_FONE]: {
    id: AppId.CLUSTER_FONE,
    component: ClusterFoneApp,
    path: '/cluster-fone',
    icon: '/app-icons/meet.webp',
    appIcon: '/app-icons/meet.webp',
    placement: ['launchpad'],
    name: 'Cluster Fone',
    group: AppGroup.COMMUNICATION,
    label: 'Cluster Fone',
    slug: 'fone',
    routeProps: {},
    // modals: [ClusterMeetCallModal],
    federationUrl: process.env.REACT_APP_CLUSTER_FONE_APP_DEV_PATH,
    // cdnUrl: process.env.REACT_APP_CLUSTER_MEET_APP_CDN_URL,
    runtimeEnvKey: 'clusterFone'
  },
  [AppId.ORDER_MANAGEMENT]: {
    id: AppId.ORDER_MANAGEMENT,
    component: OrderManagementApp,
    path: '/order-management',
    icon: '/app-icons/inventory.webp',
    appIcon: '/app-icons/inventory.webp',
    placement: ['launchpad'],
    name: 'Order Management',
    group: AppGroup.OFFICE_TOOLS,
    label: 'Order Management',
    slug: 'order_management',
    routeProps: {},
    federationUrl: process.env.REACT_APP_ORDER_MANAGEMENT_APP_DEV_PATH,
    runtimeEnvKey: 'orderManagementFrontend'
  },
  [AppId.LIVE_CHAT]: {
    id: AppId.LIVE_CHAT,
    component: LiveChatApp,
    path: '/live-chat',
    icon: '/app-icons/messenger.webp',
    appIcon: '/app-icons/messenger.webp',
    placement: ['launchpad'],
    name: 'Chat',
    group: AppGroup.COMMUNICATION,
    label: 'Chat',
    slug: 'livechat',
    routeProps: {},
    federationUrl: process.env.REACT_APP_LIVE_CHAT_V2_APP_DEV_PATH,
    runtimeEnvKey: 'livechatV2'
  },
  [AppId.EMAIL]: {
    id: AppId.EMAIL,
    component: EmailApp,
    path: '/email',
    icon: '/app-icons/mail.webp',
    appIcon: '/app-icons/mail.webp',
    placement: ['launchpad'],
    name: 'E-Mail',
    group: AppGroup.COMMUNICATION,
    label: 'Email',
    slug: 'email',
    routeProps: {
      exact: true
    },
    federationUrl: process.env.REACT_APP_EMAIL_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_EMAIL_APP_CDN_URL,
    runtimeEnvKey: 'emailApp'
  },
  /**
   * Business Tools Apps
   */
  [AppId.COMPANY_SEARCHER]: {
    id: AppId.COMPANY_SEARCHER,
    component: CompanySearcherApp,
    path: '/company-search',
    icon: '/app-icons/company-searcher.webp',
    appIcon: '/app-icons/company-searcher.webp',
    placement: ['launchpad'],
    name: 'Company Searcher',
    group: AppGroup.BUSINESS_TOOLS,
    label: 'Company Searcher',
    slug: 'company_search',
    description: i18nextContainer.t('appsList.companySearch.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_COMPANY_SEARCHER_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_COMPANY_SEARCHER_APP_CDN_URL,
    runtimeEnvKey: 'companySearcherV2'
  },
  [AppId.BULK_MAILING]: {
    id: AppId.BULK_MAILING,
    component: BulkMailingApp,
    path: '/bulk-mailing',
    icon: '/app-icons/bulk-mailing.webp',
    appIcon: '/app-icons/bulk-mailing.webp',
    name: 'Bulk Mailing',
    label: 'Bulk Mailing',
    placement: ['launchpad'],
    slug: 'bulk_mailing',
    description: i18nextContainer.t('appsList.bulkMailing.description'),
    routeProps: {},
    group: AppGroup.BUSINESS_TOOLS,
    federationUrl: process.env.REACT_APP_BULK_MAILING_APP_DEV_PATH,
    runtimeEnvKey: 'bulkMailingApp'
  },
  [AppId.SALES]: {
    id: AppId.SALES,
    component: SalesApp,
    path: '/sales',
    icon: '/app-icons/sales.webp',
    appIcon: '/app-icons/sales.webp',
    placement: ['launchpad'],
    name: 'Customers',
    group: AppGroup.BUSINESS_TOOLS,
    label: 'Customers',
    description: i18nextContainer.t('appsList.sales.description'),
    slug: 'sales_app',
    routeProps: {},
    federationUrl: process.env.REACT_APP_SALES_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_SALES_APP_CDN_URL,
    runtimeEnvKey: 'salesApp'
  },
  [AppId.TEMPLATE_MANAGER]: {
    id: AppId.TEMPLATE_MANAGER,
    component: TemplateManagerApp,
    path: '/template-manager',
    icon: '/app-icons/templates.webp',
    appIcon: '/app-icons/templates.webp',
    placement: ['launchpad'],
    name: 'Template Manager',
    group: AppGroup.BUSINESS_TOOLS,
    label: 'Templates',
    slug: 'template_manager',
    description: i18nextContainer.t('appsList.templateManager.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_TEMPLATE_MANAGER_APP_DEV_PATH,
    runtimeEnvKey: 'templateManagerApp'
  },
  [AppId.ACCOUNTING]: {
    id: AppId.ACCOUNTING,
    component: AccountingApp,
    path: '/accounting',
    icon: '/app-icons/accounting.webp',
    appIcon: '/app-icons/accounting.webp',
    placement: ['launchpad'],
    name: 'Accounting',
    group: AppGroup.BUSINESS_TOOLS,
    label: 'Accounting',
    slug: 'accounting',
    description: i18nextContainer.t('appsList.accounting.description'),
    routeProps: {},
    useAppIconInSelection: true,
    federationUrl: process.env.REACT_APP_ACCOUNTING_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_ACCOUNTING_APP_CDN_URL,
    runtimeEnvKey: 'accounting'
  },
  [AppId.AFTER_SALES]: {
    id: AppId.AFTER_SALES,
    component: AfterSalesApp,
    path: '/after-sales',
    icon: '/app-icons/crm.webp',
    appIcon: '/app-icons/crm.webp',
    slug: 'after_sales',
    placement: ['launchpad'],
    name: 'innoscripta CRM',
    group: AppGroup.BUSINESS_TOOLS,
    label: 'innoscripta CRM',
    description: i18nextContainer.t('appsList.afterSales.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_AFTER_SALES_APP_DEV_PATH,
    runtimeEnvKey: 'afterSalesApp'
  },
  [AppId.CLUSTER_CLOUD]: {
    id: AppId.CLUSTER_CLOUD,
    component: ClusterCloudApp,
    path: '/cluster-cloud',
    icon: '/app-icons/cloud.webp',
    appIcon: '/app-icons/cloud.webp',
    placement: ['launchpad'],
    name: 'Cluster Cloud',
    group: AppGroup.BUSINESS_TOOLS,
    label: 'Cluster Cloud',
    description: i18nextContainer.t('appsList.clusterCloud.description'),
    slug: 'cluster_cloud',
    routeProps: {},
    federationUrl: process.env.REACT_APP_CLUSTER_CLOUD_APP_DEV_PATH,
    runtimeEnvKey: 'clusterCloud'
  },
  [AppId.ASSET_MANAGEMENT]: {
    id: AppId.ASSET_MANAGEMENT,
    component: AssetManagementApp,
    path: '/asset-management',
    icon: '/app-icons/inventory.webp',
    appIcon: '/app-icons/inventory.webp',
    placement: ['launchpad'],
    name: 'Assets & Inventory',
    label: 'Assets & Inventory',
    description: i18nextContainer.t('appsList.assetManagement.description'),
    slug: 'asset_management',
    routeProps: {},
    group: AppGroup.BUSINESS_TOOLS,
    federationUrl: process.env.REACT_APP_ASSET_MANAGEMENT_APP_DEV_PATH,
    runtimeEnvKey: 'assetsManagementApp'
  },
  [AppId.CONTRACT_MANAGEMENT]: {
    id: AppId.CONTRACT_MANAGEMENT,
    component: ContractManagementApp,
    path: '/contract-management',
    icon: '/app-icons/contract-management.webp',
    appIcon: '/app-icons/contract-management.webp',
    placement: ['launchpad'],
    name: 'Contract Management',
    label: 'Contract Management',
    slug: 'contract_management',
    routeProps: {},
    group: AppGroup.BUSINESS_TOOLS,
    federationUrl: process.env.REACT_APP_CONTRACT_MANAGEMENT_APP_DEV_PATH,
    runtimeEnvKey: 'contractManagementApp'
  },
  [AppId.TRANSFER_PRICING]: {
    id: AppId.TRANSFER_PRICING,
    component: TransferPricingApp,
    path: '/transfer-pricing',
    icon: '/app-icons/transfer-pricing.webp',
    appIcon: '/app-icons/transfer-pricing.webp',
    placement: ['launchpad'],
    name: 'Transfer Pricing',
    label: 'Transfer Pricing',
    description: i18nextContainer.t('appsList.assetManagement.description'),
    slug: 'transfer_pricing',
    routeProps: {},
    group: AppGroup.BUSINESS_TOOLS,
    federationUrl: process.env.REACT_APP_TRANSFER_PRICING_APP_DEV_PATH,
    runtimeEnvKey: 'transferPricingApp'
  },
  [AppId.RISK_MANAGEMENT]: {
    id: AppId.RISK_MANAGEMENT,
    component: RiskManagementApp,
    path: '/risk-management',
    icon: '/app-icons/risk-management.webp',
    appIcon: '/app-icons/risk-management.webp',
    placement: ['launchpad'],
    name: 'Risk Management',
    label: 'Risk Management',
    description: i18nextContainer.t('appsList.riskManagement.description'),
    slug: 'risk_management',
    routeProps: {},
    group: AppGroup.BUSINESS_TOOLS,
    federationUrl: process.env.REACT_APP_RISK_MANAGEMENT_APP_DEV_PATH,
    runtimeEnvKey: 'riskManagementApp'
  },
  [AppId.USAGE_ANALYTICS]: {
    id: AppId.USAGE_ANALYTICS,
    component: UsageAnalyticsApp,
    path: '/usage-analytics',
    icon: '/app-icons/usage-analytics.webp',
    appIcon: '/app-icons/usage-analytics.webp',
    name: 'Usage Analytics',
    label: 'Usage Analytics',
    slug: 'usage_analytics',
    group: AppGroup.BUSINESS_TOOLS,
    placement: ['launchpad'],
    routeProps: {},
    federationUrl: process.env.REACT_APP_USAGE_ANALYTICS_APP_DEV_PATH,
    runtimeEnvKey: 'usageAnalyticsApp'
  },
  [AppId.DOC_AI]: {
    id: AppId.DOC_AI,
    component: DocAiApp,
    path: '/doc-ai',
    icon: '/app-icons/agent.webp',
    appIcon: '/app-icons/agent.webp',
    name: 'Document AI',
    label: 'Document AI',
    slug: 'doc_ai',
    group: AppGroup.BUSINESS_TOOLS,
    placement: ['launchpad'],
    routeProps: {},
    federationUrl: process.env.REACT_APP_DOC_AI_APP_DEV_PATH,
    runtimeEnvKey: 'documentAiUi'
  },
  /**
   * Configuration Apps
   */
  [AppId.BYTE_BUILDER]: {
    id: AppId.BYTE_BUILDER,
    component: ByteBuilderApp,
    path: '/byte-builder',
    icon: '/app-icons/byte-builder.webp',
    appIcon: '/app-icons/byte-builder.webp',
    placement: ['launchpad'],
    name: 'Byte Builder',
    group: AppGroup.CONFIGURATION,
    label: 'Byte Builder',
    slug: 'byte_builder',
    description: i18nextContainer.t('appsList.byteBuilder.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_BYTE_BUILDER_APP_DEV_PATH,
    runtimeEnvKey: 'byteBuilderApp'
  },
  [AppId.APPLICATION_STUDIO]: {
    id: AppId.APPLICATION_STUDIO,
    component: ApplicationStudioApp,
    path: '/application-studio',
    icon: '/app-icons/byte-builder.webp',
    appIcon: '/app-icons/byte-builder.webp',
    placement: ['launchpad'],
    name: 'Application Studio',
    group: AppGroup.CONFIGURATION,
    label: 'Application Studio',
    slug: 'application_studio',
    description: i18nextContainer.t('appsList.applicationStudio.description'),
    routeProps: {},
    federationUrl: process.env.REACT_APP_APPLICATION_STUDIO_APP_DEV_PATH,
    runtimeEnvKey: 'applicationStudio'
  },
  [AppId.INTEGRATION]: {
    id: AppId.INTEGRATION,
    component: IntegrationApp,
    path: '/integration-legacy',
    icon: '/app-icons/integrations.webp',
    appIcon: '/app-icons/integrations.webp',
    placement: ['launchpad'],
    name: 'Integration',
    group: AppGroup.CONFIGURATION,
    label: 'Integrations',
    description: i18nextContainer.t('appsList.integration.description'),
    slug: 'integration',
    routeProps: {},
    federationUrl: process.env.REACT_APP_INTEGRATION_APP_DEV_PATH,
    runtimeEnvKey: 'integration'
  },
  [AppId.INTEGRATION_V2]: {
    id: AppId.INTEGRATION_V2,
    component: IntegrationAppV2,
    path: '/integration',
    icon: '/app-icons/integrations.webp',
    appIcon: '/app-icons/integrations.webp',
    placement: ['launchpad'],
    name: 'Integration-V2',
    group: AppGroup.CONFIGURATION,
    label: 'Integration-V2',
    description: i18nextContainer.t('appsList.integration.description'),
    slug: 'integration_v2',
    routeProps: {},
    federationUrl: process.env.REACT_APP_INTEGRATION_V2_APP_DEV_PATH,
    runtimeEnvKey: 'integrationV2'
  },
  [AppId.WORKFLOWS]: {
    id: AppId.WORKFLOWS,
    component: WorkflowsApp,
    path: '/workflows',
    icon: '/app-icons/no-code.webp',
    appIcon: '/app-icons/no-code.webp',
    placement: ['launchpad'],
    name: 'No Code',
    group: AppGroup.CONFIGURATION,
    label: 'No Code',
    description: i18nextContainer.t('appsList.workflows.description'),
    slug: 'workflows',
    routeProps: {},
    federationUrl: process.env.REACT_APP_WORKFLOWS_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_WORKFLOWS_APP_CDN_URL,
    runtimeEnvKey: 'workflows'
  },
  [AppId.SETTINGS]: {
    id: AppId.SETTINGS,
    component: SettingsApp,
    path: '/settings',
    icon: '/app-icons/settings.webp',
    appIcon: '/app-icons/settings.webp',
    group: AppGroup.CONFIGURATION,
    placement: [],
    name: 'Settings',
    label: 'Settings',
    slug: 'settings',
    routeProps: {},
    federationUrl: process.env.REACT_APP_SETTINGS_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_SETTINGS_APP_CDN_URL,
    runtimeEnvKey: 'settingsApp'
  },
  [AppId.MY_ORGANIZATION]: {
    id: AppId.MY_ORGANIZATION,
    component: MyOrganizationApp,
    path: '/my-organization',
    icon: '/app-icons/my-organization.webp',
    appIcon: '/app-icons/my-organization.webp',
    placement: ['launchpad'],
    name: 'My Organization',
    label: 'My Organization',
    description: i18nextContainer.t('appsList.myOrganization.description'),
    routeProps: {},
    group: AppGroup.WORKFORCE_MANAGEMENT,
    slug: 'my_organization',
    federationUrl: process.env.REACT_APP_MY_ORGANIZATION_APP_DEV_PATH,
    runtimeEnvKey: 'myOrganizationApp'
  },
  [AppId.PROFILE]: {
    id: AppId.PROFILE,
    component: ProfileApp,
    path: '/profile',
    icon: '/app-icons/hr.webp',
    appIcon: '/app-icons/hr.webp',
    name: 'Profile',
    label: 'Profile',
    slug: 'profile',
    group: AppGroup.CONFIGURATION,
    placement: [],
    routeProps: {},
    federationUrl: process.env.REACT_APP_PROFILE_APP_DEV_PATH,
    cdnUrl: process.env.REACT_APP_PROFILE_APP_CDN_URL,
    runtimeEnvKey: 'profileApp'
  }
}

export const appConfigs: AppConfigsType = Object.fromEntries(
  Object.entries(rawAppConfigs).filter(([, config]) => isAppConfigured(config))
) as AppConfigsType

export const getAppsByGroup = (
  apps: AppConfig[],
  groupName: AppGroup,
  placement?: 'launchpad',
  pinnedAppIds?: AppId[],
  windowIds?: AppId[]
): { [K in AppGroup]?: AppConfig[] } => {
  const allAppIds = new Set<AppId>()

  if (pinnedAppIds) {
    pinnedAppIds.forEach(appId => allAppIds.add(appId))
  }

  if (windowIds) {
    windowIds.forEach(appId => allAppIds.add(appId))
  }

  const appsToFilter =
    allAppIds.size > 0 ? apps.filter(app => allAppIds.has(app.id)) : apps

  const relatedApps = appsToFilter.filter((app: AppConfig) => {
    const matchesGroup = app.group === groupName
    const matchesPlacement = placement
      ? app.placement.includes(placement)
      : true
    return matchesGroup && matchesPlacement
  })

  return { [groupName]: relatedApps }
}
